import { Box, Divider, Grid, ListItem } from '@mui/material'
import { HearingSummaryProps } from 'components/HearingSummary'
import Label from 'components/Label'
import React, { useEffect, useState } from 'react'
import { DocumentType } from 'types/document.type'
import { StyledImgViewer as ImgViewer } from './styles'

declare global {
  interface Window {
    pdfjsLib: any
  }
}

export const AttachedFileGroup: React.FC<HearingSummaryProps<DocumentType[]>> = ({ data }) => {
  const isPDF = (fileType: string) => fileType && fileType.includes('pdf')

  const b64toBlob = (b64Data: string, fileName: string, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data)
    const byteArrays = []

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)

      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)
      byteArrays.push(byteArray)
    }

    return new File(byteArrays, fileName, { type: contentType })
  }

  const handleImage = (
    base64String: string,
    fileName: string,
    contentType?: string,
  ): string | undefined => {
    const file = b64toBlob(base64String, fileName, contentType)
    if (file) {
      const imageUrl = URL.createObjectURL(file)
      return imageUrl
    }
  }

  return (
    <div className={'hidden-non-print page-break'}>
      <Label text='ARQUIVOS ANEXADOS' v='h6' bold />
      <Divider />
      {data.map(
        (item, i) =>
          item &&
          item.arquivoBase64 &&
          item.arquivoTipo &&
          item.arquivoNome && (
            <Grid key={i} container item sx={{ pt: 2, pb: 2, mt: 1 }}>
              <Box display='flex' flexDirection={'column'} width='100%'>
                <p>
                  <b>{item.descricaoIdTipoDocumento}</b> {item.numeroDocumento}
                </p>
                {isPDF(item.arquivoTipo) ? (
                  <PDFToImageList base64PDF={item.arquivoBase64} fileName={item.arquivoNome} />
                ) : (
                  <ImgViewer
                    src={handleImage(item.arquivoBase64, item.arquivoNome)}
                    alt='Selected'
                  />
                )}
              </Box>
            </Grid>
          ),
      )}
    </div>
  )
}

// Componente para converter PDF em lista de imagens
interface PDFToImageListProps {
  base64PDF: string
  fileName: string
}

const PDFToImageList: React.FC<PDFToImageListProps> = ({ base64PDF, fileName }) => {
  const [pageImages, setPageImages] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [pdfLib, setPdfLib] = useState<any>(null)

  // Carregar biblioteca PDF.js dinamicamente
  useEffect(() => {
    const loadPdfLibrary = async () => {
      if (window.pdfjsLib) {
        setPdfLib(window.pdfjsLib)
        return
      }

      try {
        // Carregar PDF.js
        const pdfJsScript = document.createElement('script')
        pdfJsScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.6.172/pdf.min.js'
        pdfJsScript.async = true

        // Esperar o script carregar
        await new Promise((resolve, reject) => {
          pdfJsScript.onload = resolve
          pdfJsScript.onerror = reject
          document.head.appendChild(pdfJsScript)
        })

        // Configurar worker após o carregamento
        if (window.pdfjsLib) {
          const PDFJS = window.pdfjsLib
          PDFJS.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.6.172/pdf.worker.min.js'
          setPdfLib(PDFJS)
        } else {
          throw new Error('PDF.js não foi carregado corretamente')
        }
      } catch (err) {
        console.error('Erro ao carregar PDF.js:', err)
        setError('Não foi possível carregar a biblioteca PDF.js')
        setLoading(false)
      }
    }

    loadPdfLibrary()
  }, [])

  // Processar o PDF quando a biblioteca estiver carregada
  useEffect(() => {
    if (!pdfLib || !base64PDF) return

    const convertPDFToImages = async () => {
      try {
        setLoading(true)

        // Converter base64 para ArrayBuffer
        const byteCharacters = atob(base64PDF)
        const byteNumbers = new Array(byteCharacters.length)

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }

        const byteArray = new Uint8Array(byteNumbers)
        const pdfData = byteArray.buffer

        // Carregar o PDF
        const pdf = await pdfLib.getDocument({ data: pdfData }).promise

        const images: string[] = []

        // Processar cada página
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum)
          const viewport = page.getViewport({ scale: 1.5 })

          // Criar canvas para renderizar a página
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')

          canvas.width = viewport.width
          canvas.height = viewport.height

          // Renderizar página no canvas
          await page.render({
            canvasContext: context!,
            viewport: viewport,
          }).promise

          // Converter canvas para URL de imagem
          const imageUrl = canvas.toDataURL('image/png')
          images.push(imageUrl)
        }

        setPageImages(images)
        setError(null)
      } catch (error) {
        console.error('Erro ao converter PDF em imagens:', error)
        setError('Ocorreu um erro ao processar o PDF. Verifique o console para mais detalhes.')
      } finally {
        setLoading(false)
      }
    }

    convertPDFToImages()
  }, [pdfLib, base64PDF])

  if (error) {
    return <Box sx={{ color: 'error.main', padding: 2 }}>{error}</Box>
  }

  if (loading) {
    return <Box>Carregando páginas do PDF...</Box>
  }

  return (
    <Box sx={{ width: '100%' }}>
      {pageImages.map((imageUrl, index) => (
        <ListItem key={index}>
          <Box
            component='img'
            src={imageUrl}
            alt={`Página ${index + 1} do PDF`}
            sx={{
              width: '100%',
              height: 'auto',
            }}
          />
        </ListItem>
      ))}
    </Box>
  )
}
